import { Form, Input, Select } from 'antd';
import React from 'react';
import authService from '../../../components/api-authorization/AuthorizeService';
import DrawerButtons from '../../../components/drawer-buttons';
import { formItemLayout420 } from '../../../config/formLayouts';
import { translations } from '../../../config/translations';
import { showError, showSuccess } from '../../../helpers/NotificationHelper';
import { getSiteManagerProjects, locationKey, parseLocationKey, toolLabel } from '../helpers';
import LocationSelect from '../location-select/LocationSelect';
import {
    ApiException,
    CreateToolTransferCommand,
    RoleEnum,
    ToolTransferDetailVm,
    ToolTransfersClient,
    ToolVm,
    ToolsClient,
    TransferEmployeeVm,
    TransferWarehouseVm,
    UpdateToolTransferCommand,
} from '../../../utils/api';

const { TextArea } = Input;

/** One page is plenty: a location never holds anywhere near this many tools. */
const ALL_TOOLS_PAGE_SIZE = 1000;

export interface TransferFormProps {
    transfer?: ToolTransferDetailVm;
    /**
     * Source location key (see locationKey), set when the form is opened for chosen tools - from
     * the tool list or a tool's page. The source is then fixed.
     */
    presetFromKey?: string;
    presetToolIds?: number[];
    onSuccess: (transfer: ToolTransferDetailVm) => void;
    onClose: () => void;
}

interface State {
    isSaving: boolean;
    /** Destination can be any warehouse or person. */
    warehouses: TransferWarehouseVm[];
    employees: TransferEmployeeVm[];
    /**
     * Source is limited to the site manager's own warehouses (a person's tools are moved by
     * SuperAdmin/Skladište); everyone else may start anywhere.
     */
    isSiteManager: boolean;
    ownProjects: string[];
    tools: ToolVm[];
    fromKey?: string;
}

class TransferForm extends React.Component<TransferFormProps, State> {
    private formRef: any = React.createRef();

    public constructor(props: TransferFormProps) {
        super(props);

        this.state = {
            isSaving: false,
            warehouses: [],
            employees: [],
            isSiteManager: false,
            ownProjects: [],
            tools: [],
            fromKey: props.transfer
                ? locationKey(props.transfer.fromWarehouseId, props.transfer.fromEmployeeId)
                : props.presetFromKey,
        };
    }

    public componentDidMount = async () => {
        const { fromKey } = this.state;
        const client = new ToolTransfersClient();

        const [warehouses, employees, userProfile] = await Promise.all([
            client.getWarehouses(),
            client.getEmployees(),
            authService.getUserProfile(),
        ]);

        this.setState({
            warehouses,
            employees,
            isSiteManager: parseInt(userProfile['userRoleId']) === RoleEnum.SiteManager,
            ownProjects: getSiteManagerProjects(userProfile),
        });

        if (fromKey) {
            await this.loadTools(fromKey);
        }
    };

    /**
     * Tools shown are the ones actually at the source. Tools already on an open transfer stay
     * in the list but are rendered disabled (spec section 3).
     */
    private loadTools = async (key: string) => {
        const { warehouseId, employeeId } = parseLocationKey(key);
        const result = await new ToolsClient().getPaginated(
            undefined,
            undefined,
            false,
            warehouseId,
            employeeId,
            undefined,
            1,
            ALL_TOOLS_PAGE_SIZE,
            'number',
            'ascend'
        );

        this.setState({ tools: result.pageItems || [] });
    };

    private handleFromChange = async (key?: string) => {
        const { transfer } = this.props;

        this.setState({ fromKey: key, tools: [] });

        // The previous selection belonged to the old source, so it cannot carry over.
        this.formRef.current?.setFieldsValue({ toolIds: [] });

        if (!key) return;

        await this.loadTools(key);

        // Re-select this transfer's own tools when the source is unchanged from the original.
        if (transfer && locationKey(transfer.fromWarehouseId, transfer.fromEmployeeId) === key) {
            this.formRef.current?.setFieldsValue({
                toolIds: (transfer.items || []).map((i) => i.toolId),
            });
        }
    };

    private handleSubmit = async (values: any) => {
        const { transfer, onSuccess } = this.props;

        this.setState({ isSaving: true });

        const from = parseLocationKey(values.from);
        const to = parseLocationKey(values.to);

        try {
            const client = new ToolTransfersClient();
            let result: ToolTransferDetailVm;

            if (transfer?.id) {
                // Assign after construction: the generated subclass constructor loses `id` (its
                // field initialiser runs after the base copies the data), and the server
                // validator needs it to tell this transfer's own tools from other transfers'.
                const command = new UpdateToolTransferCommand();
                command.id = transfer.id;
                command.fromWarehouseId = from.warehouseId;
                command.fromEmployeeId = from.employeeId;
                command.toWarehouseId = to.warehouseId;
                command.toEmployeeId = to.employeeId;
                command.note = values.note;
                command.toolIds = values.toolIds;

                result = await client.update(transfer.id, command);
            } else {
                result = await client.create(
                    new CreateToolTransferCommand({
                        fromWarehouseId: from.warehouseId,
                        fromEmployeeId: from.employeeId,
                        toWarehouseId: to.warehouseId,
                        toEmployeeId: to.employeeId,
                        note: values.note,
                        toolIds: values.toolIds,
                    })
                );
            }

            showSuccess(translations.transfers.saveSuccess);
            onSuccess(result);
        } catch (error) {
            if (error instanceof ApiException) {
                showError(error.response);
            } else {
                showError(translations.general.errorSavingData);
            }
        } finally {
            this.setState({ isSaving: false });
        }
    };

    public render(): React.ReactElement {
        const { transfer, presetFromKey, presetToolIds, onClose } = this.props;
        const { isSaving, warehouses, employees, isSiteManager, ownProjects, tools, fromKey } =
            this.state;

        // Opened for chosen tools, the source is already decided by where they are.
        const sourceLocked = !transfer && presetFromKey != null;
        const sourceWarehouses = isSiteManager
            ? warehouses.filter(
                  (w) => w.projectId != null && ownProjects.includes(w.projectId.toString())
              )
            : warehouses;

        return (
            <Form onFinish={this.handleSubmit} {...formItemLayout420} ref={this.formRef}>
                <DrawerButtons isSaving={isSaving} onCancelAction={onClose} />

                <Form.Item
                    name="from"
                    label={translations.transfers.fromWarehouse}
                    initialValue={fromKey}
                    rules={[{ required: true, message: translations.general.requiredField }]}
                >
                    <LocationSelect
                        warehouses={sourceWarehouses}
                        employees={isSiteManager ? [] : employees}
                        disabled={sourceLocked}
                        onChange={this.handleFromChange}
                    />
                </Form.Item>

                <Form.Item
                    name="to"
                    label={translations.transfers.toWarehouse}
                    initialValue={
                        transfer
                            ? locationKey(transfer.toWarehouseId, transfer.toEmployeeId)
                            : undefined
                    }
                    dependencies={['from']}
                    rules={[
                        { required: true, message: translations.general.requiredField },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || value !== getFieldValue('from')) {
                                    return Promise.resolve();
                                }

                                return Promise.reject(
                                    new Error(translations.transfers.sameWarehouseError)
                                );
                            },
                        }),
                    ]}
                >
                    {/* The source can never be the destination too, so it is not offered. */}
                    <LocationSelect
                        warehouses={warehouses}
                        employees={employees}
                        excludeKey={fromKey}
                    />
                </Form.Item>

                <Form.Item
                    name="toolIds"
                    label={translations.transfers.tools}
                    initialValue={
                        transfer ? (transfer.items || []).map((i) => i.toolId) : presetToolIds
                    }
                    rules={[{ required: true, message: translations.general.requiredField }]}
                >
                    <Select
                        mode="multiple"
                        showSearch
                        optionFilterProp="children"
                        disabled={!fromKey}
                        placeholder={
                            fromKey ? undefined : translations.transfers.selectSourceWarehouseFirst
                        }
                    >
                        {tools.map((tool: ToolVm) => {
                            // A tool already in flight elsewhere stays visible but unselectable.
                            const onOtherTransfer =
                                tool.isOnActiveTransfer &&
                                !(transfer?.items || []).some((i) => i.toolId === tool.id);

                            return (
                                <Select.Option
                                    key={tool.id}
                                    value={tool.id}
                                    disabled={onOtherTransfer}
                                >
                                    {onOtherTransfer
                                        ? toolLabel(tool) +
                                          ' (' +
                                          translations.transfers.onActiveTransfer +
                                          ')'
                                        : toolLabel(tool)}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="note"
                    label={translations.transfers.note}
                    initialValue={transfer?.note}
                >
                    <TextArea rows={4} />
                </Form.Item>
            </Form>
        );
    }
}

export default TransferForm;
